import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ServerStackIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  EllipsisVerticalIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import type { Asset, Customer } from '../context/ManagementContext';
import managementService from '../api/managementService';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import DataTable from './DataTable';
import EntitySelectorButton from './EntitySelectorButton';
import AssetDeviceManagement from './AssetDeviceManagement';
import { useDebounce } from '../hooks/useDebounce';

const assetSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  floorPlan: z.string().optional(),
  additional_info: z.string().optional(),
  customerId: z.string().min(1, 'Customer is required').uuid('Customer ID must be a valid UUID'),
});

type AssetFormData = z.infer<typeof assetSchema>;

export default function AssetManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [selectedCustomerForFilter, setSelectedCustomerForFilter] = useState<Customer | null>(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [hasParentFilter, setHasParentFilter] = useState(searchParams.get('hasParent') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'label');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'asc');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);
  const [isCustomerFilterSelectorOpen, setIsCustomerFilterSelectorOpen] = useState(false);
  const [isParentSelectorOpen, setIsParentSelectorOpen] = useState(false);
  const [selectedCustomerForForm, setSelectedCustomerForForm] = useState<Customer | null>(null);
  const [selectedParentForForm, setSelectedParentForForm] = useState<Asset | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDeviceManagementOpen, setIsDeviceManagementOpen] = useState(false);
  const [assetForDeviceManagement, setAssetForDeviceManagement] = useState<Asset | null>(null);
  const queryClient = useQueryClient();

  // Function to update URL with current filter state
  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (hasParentFilter !== 'all') params.set('hasParent', hasParentFilter);
    if (sortBy !== 'label') params.set('sortBy', sortBy);
    if (sortOrder !== 'asc') params.set('sortOrder', sortOrder);
    if (selectedCustomerForFilter) params.set('customerId', selectedCustomerForFilter.id);
    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, statusFilter, hasParentFilter, sortBy, sortOrder, selectedCustomerForFilter, setSearchParams]);

  // Initialize entities from URL parameters on component mount
  useEffect(() => {
    const initializeFromUrl = async () => {
      const customerId = searchParams.get('customerId');
      
      if (customerId) {
        if (customers.length > 0) {
          const customer = customers.find(c => c.id === customerId);
          if (customer) {
            setSelectedCustomerForFilter(customer);
          } else {
            // If not found in loaded data, fetch it
            try {
              const fetchedCustomer = await managementService.customer.getById(customerId);
              setSelectedCustomerForFilter(fetchedCustomer);
            } catch (error) {
              console.warn('Failed to load customer from URL:', error);
            }
          }
        } else {
          // Data not loaded yet, try fetching directly
          try {
            const fetchedCustomer = await managementService.customer.getById(customerId);
            setSelectedCustomerForFilter(fetchedCustomer);
          } catch (error) {
            console.warn('Failed to load customer from URL:', error);
          }
        }
      }
    };
    
    initializeFromUrl();
  }, [searchParams]);


  // Update URL parameters whenever filter state changes
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  const { data: assetsData, isLoading, error } = useQuery({
    queryKey: ['assets', currentPage, debouncedSearchTerm, selectedCustomerForFilter?.id, statusFilter, hasParentFilter, sortBy, sortOrder],
    queryFn: () => managementService.asset.getAll({
      page: currentPage,
      limit: 10,
      search: debouncedSearchTerm || undefined,
      customerId: selectedCustomerForFilter?.id || undefined,
      status: statusFilter as 'active' | 'inactive' | 'all',
      sortBy: sortBy as 'label' | 'createdAt' | 'updatedAt',
      sortOrder: sortOrder as 'asc' | 'desc'
    }),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => managementService.customer.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const { data: allAssets = [] } = useQuery({
    queryKey: ['assets-all'],
    queryFn: () => managementService.asset.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const createMutation = useMutation({
    mutationFn: (data: AssetFormData) => managementService.asset.create({
      ...data,
      isActive: true,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assets-all'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Asset creation error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.error || t('asset.failedToCreate');
      setErrorMessage(errorMsg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssetFormData }) =>
      managementService.asset.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assets-all'] });
      setIsEditOpen(false);
      setSelectedAsset(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => managementService.asset.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assets-all'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const { register, handleSubmit, formState: { errors }, reset, setValue, unregister, clearErrors } = useForm<AssetFormData>({
    resolver: zodResolver(assetSchema),
  });

  // Reset page when search or filters change (but not on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, selectedCustomerForFilter, statusFilter, hasParentFilter]);
  
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCustomerForFilter(null);
    setStatusFilter('all');
    setHasParentFilter('all');
    setSortBy('label');
    setSortOrder('asc');
  }, []);

  const resetForm = () => {
    reset({
      label: '',
      floorPlan: '',
      additional_info: '',
      customerId: ''
    });
    setSelectedAsset(null);
    setSelectedCustomerForForm(null);
    setSelectedParentForForm(null);
    setErrorMessage(null);
  };

  const handleCreate = (data: AssetFormData) => {
    const formData = {
      ...data,
      parentId: selectedParentForForm?.id ?? null,
    };
    createMutation.mutate(formData);
  };

  const handleEdit = (asset: Asset) => {
    setSelectedAsset(asset);
    setValue('label', asset.label);
    setValue('floorPlan', asset.floorPlan || '');
    setValue('additional_info', asset.additional_info || '');
    setValue('customerId', asset.customerId);
    
    // Set selected entities for the form
    const selectedCustomer = customers.find(c => c.id === asset.customerId);
    const selectedParent = allAssets.find(a => a.id === asset.parentId);
    setSelectedCustomerForForm(selectedCustomer || null);
    setSelectedParentForForm(selectedParent || null);
    
    setIsEditOpen(true);
  };

  const handleUpdate = (data: AssetFormData) => {
    if (selectedAsset) {
      const formData = {
        ...data,
        parentId: selectedParentForForm?.id ?? null,
      };
      updateMutation.mutate({ id: selectedAsset.id, data: formData });
    }
  };

  const handleDelete = (asset: Asset) => {
    if (window.confirm(t('asset.confirmDelete', { label: asset.label }))) {
      deleteMutation.mutate(asset.id);
    }
  };


  const assetColumns = [
    {
      key: 'asset',
      header: t('asset.asset'),
      width: '280px',
      render: (asset: Asset) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 mr-3">
            <div className={`h-2 w-2 rounded-full ${asset.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div 
              className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-purple-600 transition-colors text-left"
              onClick={() => handleEdit(asset)}
            >
              {asset.label}
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <ServerStackIcon className="h-3 w-3 mr-1" />
              <span className="truncate">{asset.additional_info || 'No description'}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'customer',
      header: t('asset.customer'),
      render: (asset: Asset) => (
        <div className="text-sm">
          {asset.customer?.name ? (
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-3 w-3 text-blue-500 mr-1 flex-shrink-0" />
              <span className="text-gray-900 truncate">{asset.customer.name}</span>
            </div>
          ) : (
            <span className="text-gray-400 text-xs">Unassigned</span>
          )}
        </div>
      )
    },
    {
      key: 'parent',
      header: t('asset.parentAsset'),
      render: (asset: Asset) => (
        <div className="text-sm text-gray-600 truncate max-w-xs">
          {asset.parent?.label || '—'}
        </div>
      )
    },
    {
      key: 'lastUpdated',
      header: t('common.lastUpdated'),
      width: '100px',
      render: (asset: Asset) => (
        <div className="text-xs text-gray-500">
          {new Date(asset.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit'
          })}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      sticky: 'right' as const,
      width: '60px',
      render: (asset: Asset) => (
        <div className="flex items-center justify-end">
          <Menu as="div" className="relative z-50">
            <MenuButton className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </MenuButton>
            <MenuItems anchor="bottom end" className="w-48 bg-white rounded-md shadow-xl border border-gray-200 py-1 z-[9999] focus:outline-none">
              <MenuItem>
                <button
                  onClick={() => {
                    setAssetForDeviceManagement(asset);
                    setIsDeviceManagementOpen(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center data-[focus]:bg-gray-100"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Manage Devices
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handleEdit(asset)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center data-[focus]:bg-gray-100"
                >
                  <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Edit
                </button>
              </MenuItem>
              <hr className="my-1" />
              <MenuItem>
                <button
                  onClick={() => handleDelete(asset)}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center data-[focus]:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 mr-3 text-red-400" />
                  Delete
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('asset.title')}</h1>
        <button
          onClick={() => {
            resetForm();
            setIsCreateOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('asset.addAsset')}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('asset.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            
            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Customer:</label>
                <button
                  type="button"
                  onClick={() => setIsCustomerFilterSelectorOpen(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {selectedCustomerForFilter ? (
                    <>
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-blue-600" />
                      {selectedCustomerForFilter.name}
                    </>
                  ) : (
                    <>
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      All Customers
                    </>
                  )}
                </button>
                {selectedCustomerForFilter && (
                  <button
                    type="button"
                    onClick={() => setSelectedCustomerForFilter(null)}
                    className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    title="Clear customer filter"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                >
                  <option value="all">{t('common.all')} {t('common.status')}</option>
                  <option value="active">{t('common.active')} Only</option>
                  <option value="inactive">{t('common.inactive')} Only</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Hierarchy:</label>
                <select
                  value={hasParentFilter}
                  onChange={(e) => setHasParentFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                >
                  <option value="all">{t('asset.allAssets')}</option>
                  <option value="parent">{t('asset.topLevelAssets')}</option>
                  <option value="child">{t('asset.childAssets')}</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">{t('asset.sortBy')}:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                >
                  <option value="label">{t('asset.sortByLabel')}</option>
                  <option value="createdAt">{t('asset.sortByCreatedAt')}</option>
                  <option value="updatedAt">{t('asset.sortByUpdatedAt')}</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                >
                  <option value="asc">{t('asset.ascending')}</option>
                  <option value="desc">{t('asset.descending')}</option>
                </select>
              </div>
              
              {(searchTerm || selectedCustomerForFilter || statusFilter !== 'all' || hasParentFilter !== 'all' || sortBy !== 'label' || sortOrder !== 'asc') && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-green-600 hover:text-green-800 font-medium"
                >
                  {t('common.clearFilters')}
                </button>
              )}
            </div>
            
            {/* Results Summary */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <div>
                {t('asset.showingCount', { count: assetsData?.data?.length || 0, total: assetsData?.pagination?.total || 0 })}
                {debouncedSearchTerm && (
                  <span className="ml-2">{t('asset.searchingFor', { term: debouncedSearchTerm })}</span>
                )}
              </div>
              {isLoading && (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                  <span>{t('asset.searching')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Asset List */}
      <DataTable
        data={assetsData?.data || []}
        columns={assetColumns}
        isLoading={isLoading}
        error={error}
        pagination={assetsData?.pagination}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        emptyStateIcon={ServerStackIcon}
        emptyStateTitle={t('asset.noAssets')}
        emptyStateDescription={t('asset.noAssetsDescription')}
      />

      {/* Create Asset Dialog */}
      <Dialog open={isCreateOpen} onClose={() => {
        setIsCreateOpen(false);
        resetForm();
      }} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-5xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                {t('asset.createNewAsset')}
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                {t('asset.addAssetDescription')}
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              {/* Error message */}
              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit(handleCreate)} className="space-y-8">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      {t('asset.basicInformation')}
                    </h3>
                    
                    <div>
                      <label htmlFor="label" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.assetLabel')} *
                      </label>
                      <input
                        type="text"
                        id="label"
                        {...register('label')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder={t('asset.enterAssetLabel')}
                      />
                      {errors.label && (
                        <p className="mt-2 text-sm text-red-600">{errors.label.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="floorPlan" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.floorPlan')}
                      </label>
                      <input
                        type="text"
                        id="floorPlan"
                        {...register('floorPlan')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder={t('asset.enterFloorPlan')}
                      />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      {t('asset.assignment')}
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.customer')} *
                      </label>
                      <input type="hidden" {...register('customerId')} />
                      <EntitySelectorButton
                        selectedEntity={selectedCustomerForForm}
                        onSelect={() => setIsCustomerSelectorOpen(true)}
                        onClear={() => {
                          setSelectedCustomerForForm(null);
                          unregister('customerId');
                          clearErrors('customerId');
                        }}
                        placeholder={t('asset.selectCustomer')}
                        icon={BuildingOfficeIcon}
                        getEntityName={(customer) => customer.name}
                        getEntityDescription={(customer) => customer.description || 'No description'}
                        iconColor="text-blue-600"
                      />
                      {errors.customerId && (
                        <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.parentAsset')}
                      </label>
                      <EntitySelectorButton
                        selectedEntity={selectedParentForForm}
                        onSelect={() => setIsParentSelectorOpen(true)}
                        onClear={() => setSelectedParentForForm(null)}
                        placeholder={t('asset.noParent')}
                        icon={ServerStackIcon}
                        getEntityName={(asset) => asset.label}
                        getEntityDescription={(asset) => asset.additional_info || 'No description'}
                        iconColor="text-green-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t('asset.additionalInfo')}
                  </h3>
                  
                  <div>
                    <label htmlFor="additional_info" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('asset.additionalInfo')}
                    </label>
                    <textarea
                      id="additional_info"
                      rows={4}
                      {...register('additional_info')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder={t('asset.enterAdditionalInfo')}
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreateOpen(false);
                      resetForm();
                    }}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createMutation.isPending ? t('asset.creatingAsset') : t('asset.createAsset')}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={isEditOpen} onClose={() => {
        setIsEditOpen(false);
        resetForm();
      }} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-5xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                {t('asset.editAsset')}
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                {t('asset.updateAssetDescription')}
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleUpdate)} className="space-y-8">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      {t('asset.basicInformation')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asset ID
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedAsset?.id || ''}
                          readOnly
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-text select-all"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedAsset?.id) {
                              navigator.clipboard.writeText(selectedAsset.id);
                              // You could add a toast notification here
                            }
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded"
                          title="Copy asset ID"
                        >
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Read-only identifier for this asset</p>
                    </div>
                    
                    <div>
                      <label htmlFor="edit-label" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.assetLabel')} *
                      </label>
                      <input
                        type="text"
                        id="edit-label"
                        {...register('label')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder={t('asset.enterAssetLabel')}
                      />
                      {errors.label && (
                        <p className="mt-2 text-sm text-red-600">{errors.label.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-floorPlan" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.floorPlan')}
                      </label>
                      <input
                        type="text"
                        id="edit-floorPlan"
                        {...register('floorPlan')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder={t('asset.enterFloorPlan')}
                      />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      {t('asset.assignment')}
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.customer')} *
                      </label>
                      <input type="hidden" {...register('customerId')} />
                      <EntitySelectorButton
                        selectedEntity={selectedCustomerForForm}
                        onSelect={() => setIsCustomerSelectorOpen(true)}
                        onClear={() => {
                          setSelectedCustomerForForm(null);
                          unregister('customerId');
                          clearErrors('customerId');
                        }}
                        placeholder={t('asset.selectCustomer')}
                        icon={BuildingOfficeIcon}
                        getEntityName={(customer) => customer.name}
                        getEntityDescription={(customer) => customer.description || 'No description'}
                        iconColor="text-blue-600"
                      />
                      {errors.customerId && (
                        <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('asset.parentAsset')}
                      </label>
                      <EntitySelectorButton
                        selectedEntity={selectedParentForForm}
                        onSelect={() => setIsParentSelectorOpen(true)}
                        onClear={() => setSelectedParentForForm(null)}
                        placeholder={t('asset.noParent')}
                        icon={ServerStackIcon}
                        getEntityName={(asset) => asset.label}
                        getEntityDescription={(asset) => asset.additional_info || 'No description'}
                        iconColor="text-green-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t('asset.additionalInfo')}
                  </h3>
                  
                  <div>
                    <label htmlFor="edit-additional_info" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('asset.additionalInfo')}
                    </label>
                    <textarea
                      id="edit-additional_info"
                      rows={4}
                      {...register('additional_info')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder={t('asset.enterAdditionalInfo')}
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditOpen(false);
                      resetForm();
                    }}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateMutation.isPending ? t('asset.updatingAsset') : t('asset.updateAsset')}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Advanced Entity Selectors */}
      <AdvancedEntitySelector
        isOpen={isCustomerSelectorOpen}
        onClose={() => setIsCustomerSelectorOpen(false)}
        onSelect={(entity) => {
          const customer = entity as Customer;
          console.log('Selected customer in selector:', customer);
          setSelectedCustomerForForm(customer);
          setValue('customerId', customer.id, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
          console.log('Set customerId to:', customer.id);
        }}
        entityType="customer"
        title="Select Customer"
        selectedId={selectedCustomerForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isParentSelectorOpen}
        onClose={() => setIsParentSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedParentForForm(entity as Asset);
        }}
        entityType="asset"
        title="Select Parent Asset"
        selectedId={selectedParentForForm?.id}
        customerFilter={selectedCustomerForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isCustomerFilterSelectorOpen}
        onClose={() => setIsCustomerFilterSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedCustomerForFilter(entity as Customer);
        }}
        entityType="customer"
        title="Filter by Customer"
        selectedId={selectedCustomerForFilter?.id}
      />


      {/* Device Management Modal */}
      {isDeviceManagementOpen && assetForDeviceManagement && (
        <AssetDeviceManagement
          asset={assetForDeviceManagement}
          onClose={() => {
            setIsDeviceManagementOpen(false);
            setAssetForDeviceManagement(null);
          }}
        />
      )}
    </div>
  );
}
